<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import { computed, ref } from 'vue';

const props = defineProps({
  rows: { type: Array, default: () => [] },
  title: { type: String, default: 'Custos da compra' },
  subtitle: { type: String, default: '' },
  recalculating: { type: Boolean, default: false },
});

const activeDialog = ref(null);

const regularRows = computed(() =>
  props.rows.filter(row => row.kind !== 'TOTAL_COST')
);

const totalRow = computed(
  () => props.rows.find(row => row.kind === 'TOTAL_COST') || null
);

const formatBRL = value =>
  value == null || value === ''
    ? '-'
    : new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        maximumFractionDigits: 0,
      }).format(Number(value));
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <section class="rounded-lais border border-lais-border bg-white p-4">
    <div class="flex flex-wrap items-start justify-between gap-3">
      <div>
        <p
          class="text-[12px] font-semibold uppercase tracking-wide text-lais-text-muted"
        >
          {{ title }}
        </p>
        <p v-if="subtitle" class="mt-1 text-[12px] text-lais-text-muted">
          {{ subtitle }}
        </p>
      </div>
      <span
        v-if="recalculating"
        class="inline-flex items-center gap-1 rounded-full bg-lais-bg-soft px-2 py-1 text-[11px] font-semibold text-lais-text-muted"
      >
        <span
          class="i-lucide-loader-2 size-3 animate-spin"
          aria-hidden="true"
        />
        Recalculando
      </span>
    </div>

    <div class="mt-4 grid gap-2 text-[13px]">
      <div
        v-for="row in regularRows"
        :key="row.kind"
        class="flex items-start justify-between gap-3 rounded-lais-sm border border-lais-border-soft px-3 py-2"
      >
        <div class="min-w-0">
          <div class="flex items-center gap-1.5">
            <p class="font-semibold text-lais-text">{{ row.label }}</p>
            <button
              v-if="row.dialog"
              type="button"
              class="grid size-6 place-items-center rounded-full text-lais-text-muted transition-colors hover:bg-lais-bg-soft hover:text-lais-primary"
              :aria-label="`Ver detalhes de ${row.label}`"
              @click="activeDialog = row.dialog"
            >
              <span class="i-lucide-info size-3.5" aria-hidden="true" />
            </button>
          </div>
          <p
            v-if="row.description"
            class="mt-0.5 text-[12px] text-lais-text-muted"
          >
            {{ row.description }}
          </p>
        </div>
        <strong class="shrink-0 text-lais-text">
          {{ formatBRL(row.value) }}
        </strong>
      </div>

      <div
        v-if="totalRow"
        class="flex items-center justify-between gap-3 border-t border-dashed border-lais-border pt-3"
      >
        <span class="font-semibold text-lais-text">{{ totalRow.label }}</span>
        <strong class="text-lais-primary">{{
          formatBRL(totalRow.value)
        }}</strong>
      </div>
    </div>

    <div
      v-if="activeDialog"
      class="fixed inset-0 z-50 grid place-items-center bg-black/40 px-4"
      role="dialog"
      aria-modal="true"
      @click.self="activeDialog = null"
    >
      <div
        class="w-full max-w-[460px] rounded-lais border border-lais-border bg-white p-5 shadow-lais-card"
      >
        <div class="flex items-start justify-between gap-4">
          <div>
            <h3 class="text-[17px] font-semibold text-lais-text">
              {{ activeDialog.title }}
            </h3>
            <p
              v-if="activeDialog.description"
              class="mt-2 text-[13px] leading-relaxed text-lais-text-body"
            >
              {{ activeDialog.description }}
            </p>
          </div>
          <button
            type="button"
            class="grid size-8 shrink-0 place-items-center rounded-full text-lais-text-muted transition-colors hover:bg-lais-bg-soft hover:text-lais-primary"
            aria-label="Fechar"
            @click="activeDialog = null"
          >
            <span class="i-lucide-x size-4" aria-hidden="true" />
          </button>
        </div>
        <ul
          v-if="activeDialog.items?.length"
          class="mt-4 grid gap-2 text-[13px] text-lais-text-body"
        >
          <li v-for="item in activeDialog.items" :key="item" class="flex gap-2">
            <span
              class="i-lucide-check mt-0.5 size-4 shrink-0 text-lais-primary"
              aria-hidden="true"
            />
            <span>{{ item }}</span>
          </li>
        </ul>
      </div>
    </div>
  </section>
</template>
